import * as React from "react";
import { useContext, useEffect, useState, useRef } from "react";
import { Form, Formik } from "formik";
import { Box, Grid, FormControlLabel, Switch, Button } from "@mui/material";
import Title from "../../Components/Title";
import { useNavigate } from "react-router-dom";
import { PreferencesContext } from "../../PreferencesContext";
import { UpdateStickyHeader } from '../../Services/StickyHeaderService';
import ShortcutButton from "../../Components/ShortcutButton";
import LoadingButton from "@mui/lab/LoadingButton";
import { AlertType } from "../../shared/Modal/AlertType";
import { useColorMode } from "../../Theme/ColorModeContext";

interface IStickyHeader {
  enableStickyHeader: boolean;
}

export default function Preferences() {
  const formikRef = useRef<any>(null);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const { mode, setColorMode } = useColorMode();
  console.log("usercontext",userContext);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState<IStickyHeader>({
    enableStickyHeader: userContext?.enableStickyHeader || false,
  });
  const navigate = useNavigate();
  
  const handleSubmit = async (values: IStickyHeader) => {
    setSubmitting(true);

    try {
      const payload = {
        consumerId: userContext.consumerId,
        userId: userContext.userId,
        enableStickyHeader: values.enableStickyHeader,
        modifiedBy: userContext.userId,
      };

      const response = await UpdateStickyHeader(payload);

      const isSuccess =
        response?.data !== null && response?.data !== undefined && !response?.error;

      if (isSuccess) {
        let enabledValue = values.enableStickyHeader;
        if (response?.data?.enableStickyHeader !== undefined) {
          enabledValue = response.data.enableStickyHeader;
        }

        updateUserContext({
          ...userContext,
          enableStickyHeader: enabledValue,
          isAlert: true,
          alertMessage: `Sticky Header ${enabledValue ? "Enabled" : "Disabled"} Successfully`,
          alertType: AlertType.Success,
        });

        navigate("/configuration");
      } else {
        throw new Error(response?.message || "Save failed");
      }
    } catch (error: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: error?.message || "Error while saving",
        alertType: AlertType.Error,
      });
    } finally {
      setSubmitting(false);
    }
  };

 useEffect(() => {
  if (
    userContext?.enableStickyHeader !== undefined &&
    formikRef.current
  ) {
    formikRef.current.setFieldValue(
      "enableStickyHeader",
      userContext.enableStickyHeader
    );
  }
}, [userContext?.enableStickyHeader]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        navigate("/configuration");
      }
    };

    const handleShortcut = (event: any) => {
      if (event.detail && event.detail.type === "save" && formikRef.current) {
        formikRef.current.submitForm();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("appShortcut", handleShortcut);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("appShortcut", handleShortcut);
    };
  }, [navigate]);

  const handleCancel = () => {
    navigate("/configuration");
  };

  return (
    <>
      <Title pageTitle="Preferences" />

      <Box sx={{ padding: "16px 24px" }}>
        <Grid container>
          <Grid item>
            <h4>Preferences</h4>
          </Grid>
        </Grid>

        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <Form>
              <Grid container spacing={3}>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mode === "dark"}
                        onChange={(e) => {
                          setColorMode(e.target.checked ? "dark" : "light");
                        }}
                      />
                    }
                    label="Dark Mode"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={userContext?.enableStickyHeader || false}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          // Update context immediately
                          updateUserContext({
                            ...userContext,
                            enableStickyHeader: newValue,
                          });
                          // Update Formik for save
                          props.setFieldValue("enableStickyHeader", newValue);
                        }}
                      />
                    }
                    label="Enable Sticky Header"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-start",
                      marginTop: 2,
                    }}
                  >
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={isSubmitting}
                    >
                      <ShortcutButton label="F10" variant="dark" />
                      Save
                    </LoadingButton>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleCancel}
                    >
                      <ShortcutButton label="ESC" />
                      Cancel
                    </Button>
                  </Box>
                </Grid>

              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
}